import { Node, mergeAttributes } from '@tiptap/core';
import { ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import React, { useRef, useState } from 'react';

const ResizableImageView = ({ node, updateAttributes, selected }) => {
    const { src, alt, width, height } = node.attrs;
    const imgRef = useRef(null);
    const [isResizing, setIsResizing] = useState(false);

    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsResizing(true);
        const startX = e.clientX;
        const startY = e.clientY;
        const startWidth = imgRef.current.offsetWidth;
        const startHeight = imgRef.current.offsetHeight;

        const handleMouseMove = (moveEvent) => {
            const deltaX = moveEvent.clientX - startX;
            const deltaY = moveEvent.clientY - startY;
            const newWidth = Math.max(50, startWidth + deltaX);
            const newHeight = Math.max(50, startHeight + deltaY);
            updateAttributes({ width: newWidth, height: newHeight });
        };

        const handleMouseUp = () => {
            setIsResizing(false);
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    return (
        <NodeViewWrapper as="span" style={{ display: 'inline-block', position: 'relative', maxWidth: '100%' }}>
            <img 
                ref={imgRef}
                src={src} 
                alt={alt} 
                style={{ 
                    width: width ? `${width}px` : '100%', 
                    maxWidth: '100%',
                    height: height ? `${height}px` : 'auto',
                    border: selected ? '2px solid #3b82f6' : '2px solid transparent',
                    display: 'block',
                    borderRadius: '6px',
                    objectFit: 'contain'
                }} 
            />
            {selected && (
                <div 
                    onMouseDown={handleMouseDown}
                    style={{
                        position: 'absolute',
                        right: '-5px',
                        bottom: '-5px',
                        width: '12px',
                        height: '12px',
                        backgroundColor: '#3b82f6',
                        cursor: 'se-resize',
                        borderRadius: '2px'
                    }}
                />
            )}
        </NodeViewWrapper>
    );
};

export const ResizableImageExtension = Node.create({
    name: 'resizableImage',
    group: 'inline',
    inline: true,
    draggable: true,

    addAttributes() {
        return {
            src: { default: null },
            alt: { default: null },
            width: { default: null },
            height: { default: null },
        };
    },

    parseHTML() {
        return [
            {
                tag: 'img',
                getAttrs: node => ({
                    src: node.getAttribute('src'),
                    alt: node.getAttribute('alt'),
                    width: parseInt(node.style.width) || node.getAttribute('width'),
                    height: parseInt(node.style.height) || node.getAttribute('height'),
                }),
            },
        ];
    },

    renderHTML({ HTMLAttributes }) {
        return ['img', mergeAttributes(HTMLAttributes)];
    },

    addCommands() {
        return {
            setImage: options => ({ commands }) => {
                return commands.insertContent({
                    type: this.name,
                    attrs: options,
                });
            },
        };
    },

    addNodeView() {
        return ReactNodeViewRenderer(ResizableImageView);
    },
});
